import { moveToFulfilled, update } from '@ms/yammer-data-support';
import { FileState } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import { DeleteVideoFileFulfilledAction } from '../../domains/deleteFile/actions.types';
import {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import { FileUploadJobCompletedAction } from '../../domains/fileUploadJobs/actions.types';
import { GroupSidebarLoadFulfilledAction } from '../../domains/groupSidebarLoad/actions.types';
import { MessageLanguageSpecificContentLoadAction } from '../../domains/messageLanguageSpecificContent/actions.types';
import {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import {
  ThreadPinToEventFeedFulfilledAction,
  ThreadPinToGroupFeedFulfilledAction,
  ThreadPinToTeamsMeetingFeedFulfilledAction,
  ThreadUnpinFromEventFeedFulfilledAction,
  ThreadUnpinFromGroupFeedFulfilledAction,
  ThreadUnpinFromTeamsMeetingFeedFulfilledAction,
} from '../../domains/pinnedThreads/actions.types';
import {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import { ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';

import { VideoFile, VideoFiles } from './types';

export type VideoFileAction =
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | ThreadPinToEventFeedFulfilledAction
  | ThreadPinToGroupFeedFulfilledAction
  | ThreadPinToTeamsMeetingFeedFulfilledAction
  | ThreadUnpinFromEventFeedFulfilledAction
  | ThreadUnpinFromGroupFeedFulfilledAction
  | ThreadUnpinFromTeamsMeetingFeedFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishEditMessageFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | FileUploadJobCompletedAction
  | GroupSidebarLoadFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | DeleteVideoFileFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | ThreadSharesLoadFulfilledAction
  | MessageLanguageSpecificContentLoadAction;

const tombstoneDeletedFile = (state: VideoFiles, { payload }: DeleteVideoFileFulfilledAction) => {
  const fileToDelete = state.fulfilled.byId[payload.id];

  const tombstonedFile: VideoFile = {
    id: payload.id,
    databaseId: fileToDelete.databaseId,
    displayName: '',
    downloadLink: '',
    fileDescription: '',
    embeddablePreviewUrl: '',
    state: FileState.DELETED,
    width: 0,
    height: 0,
    streamUrlProvider: '',
    typename: 'SHAREPOINT',
  };

  return update(state, payload.id, tombstonedFile);
};

const initialState: VideoFiles = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const reducer: Reducer<VideoFiles, VideoFileAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'THREAD.PIN_TO_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'THREAD.PIN_TO_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'THREAD.UNPIN_FROM_EVENT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'THREAD.UNPIN_FROM_GROUP_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'FILE_UPLOAD_JOB.COMPLETED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'GROUP_SIDEBAR.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'SEARCH.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'FILE.VIDEO_DELETE_FULFILLED':
      return tombstoneDeletedFile(state, action);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    case 'MESSAGE_LANGUAGE_SPECIFIC_CONTENT.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.videoFiles);
    default:
      return state;
  }
};

export default reducer;
